import { Link, Navigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrders } from "../queries/ordersQueries";
import { useMenuItems } from "../queries/menuQueries";
import { formatPrice } from "../utils/currency";
import { useAuthStore } from "../stores/authStore";
import { getMenuUrl } from "../utils/restaurant";
import { useRestaurantId } from "../utils/restaurant";
import {
  Container,
  Title,
  Stack,
  Paper,
  Group,
  Text,
  Badge,
  Center,
  Loader,
  Alert,
  Button,
} from "@mantine/core";
import { IconAlertCircle, IconReceipt } from "@tabler/icons-react";

const statusColors: Record<string, string> = {
  pending: "yellow",
  confirmed: "cyan",
  preparing: "orange",
  ready: "green",
  completed: "gray",
  cancelled: "red",
};

export default function OrdersPage() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { data: orders, isLoading, error } = useOrders();
  const { data: menuItems } = useMenuItems(false);

  // Get restaurant ID from first menu item
  const restaurantId = useRestaurantId();

  // Only allow admin@smagly.com to access this page
  // This page is hidden from navigation but kept for future use
  if (!isAuthenticated || user?.email !== "admin@smagly.com") {
    return <Navigate to="/" replace />;
  }

  const [clearedAt, setClearedAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem("ordersClearedAt");
    } catch {
      return null;
    }
  });

  const menuById = useMemo(() => {
    const map: Record<string, { name: string }> = {};
    (menuItems || []).forEach((m) => {
      map[m.id] = { name: m.name };
    });
    return map;
  }, [menuItems]);

  const visibleOrders = useMemo(() => {
    if (!orders) return [];

    // Filter to only show orders belonging to the current user (exclude guest orders)
    // Guest orders have user_id === null
    const userOrders = orders.filter((o) => o.user_id === user?.id);

    if (!clearedAt) return userOrders;
    const clearedTime = new Date(clearedAt).getTime();
    return userOrders.filter(
      (o) => new Date(o.created_on).getTime() > clearedTime
    );
  }, [orders, clearedAt, user?.id]);

  const handleClearOrders = () => {
    const now = new Date().toISOString();
    try {
      localStorage.setItem("ordersClearedAt", now);
    } catch {
      // ignore storage errors
    }
    setClearedAt(now);
  };

  const getOrderTitle = (order: {
    items: Array<{ menu_item_id: string }>;
    id: string;
  }) => {
    if (!order.items || order.items.length === 0) {
      return `${t("orders.orderNumber")}${order.id}`;
    }
    const firstItemName =
      menuById[order.items[0].menu_item_id]?.name ||
      `${t("orders.orderNumber")}${order.id}`;
    if (order.items.length === 1) return firstItemName;
    const restCount = order.items.length - 1;
    const restLabel =
      restCount > 1 ? t("orders.itemsPlural") : t("orders.items");
    return `${firstItemName} + ${restCount} ${restLabel}`;
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title={t("orders.error")}
        >
          {t("orders.errorDescription")}
        </Alert>
      </Container>
    );
  }

  if (!visibleOrders || visibleOrders.length === 0) {
    return (
      <Container py="xl">
        <Group justify="space-between" align="center" mb="lg">
          <Title order={1}>{t("orders.title")}</Title>
          <Button variant="light" color="red" onClick={handleClearOrders}>
            Clear orders
          </Button>
        </Group>
        <Center h={300}>
          <Stack align="center" gap="md">
            <IconReceipt size={64} stroke={1.5} color="gray" />
            <Text c="dimmed">{t("orders.empty")}</Text>
            <Button
              component={Link}
              to={getMenuUrl(restaurantId)}
              color="brand"
            >
              {t("orders.browseMenu")}
            </Button>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container py="md">
      <Group justify="space-between" align="center" mb="lg">
        <Title order={1}>{t("orders.title")}</Title>
        <Button variant="light" color="red" onClick={handleClearOrders}>
          Clear orders
        </Button>
      </Group>

      <Stack gap="md">
        {visibleOrders.map((order) => (
          <Paper
            key={order.id}
            component={Link}
            to={`/orders/${order.id}`}
            withBorder
            p="md"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Group justify="space-between" mb="sm">
              <div>
                <Text fw={600} size="lg">
                  {getOrderTitle(order)}
                </Text>
                <Text size="sm" c="dimmed">
                  {new Date(order.created_on).toLocaleString()}
                </Text>
              </div>
              <Badge color={statusColors[order.status]} size="lg">
                {t(`orders.status.${order.status}`)}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="sm">
                {order.items.length}{" "}
                {order.items.length > 1
                  ? t("orders.itemsPlural")
                  : t("orders.items")}
              </Text>
              <Text fw={700} size="lg">
                {formatPrice(order.total_amount)}
              </Text>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Container>
  );
}
